import React, { useRef, useState } from 'react'
import styles from './MicrositeRichTextEditor.module.css'
import { Bold, Italic, Link2, List, Heading2, Code2, Eye } from 'lucide-react'

/**
 * MicrositeRichTextEditor
 * Lightweight HTML rich text editor for blog post bodies, using
 * contentEditable + execCommand for basic formatting, with a
 * source/HTML view toggle (JetBrains Mono) for direct editing.
 * Intentionally dependency-free — no external rich text library.
 *
 * Usage:
 *   <MicrositeRichTextEditor
 *     label="Body"
 *     value={form.body}
 *     onChange={(html) => updateField('body', html)}
 *   />
 */
const MicrositeRichTextEditor = ({ label, value, onChange, placeholder = 'Write your post…' }) => {
    const editorRef = useRef(null)
    const [sourceMode, setSourceMode] = useState(false)

    const exec = (command, arg) => {
        document.execCommand(command, false, arg)
        if (editorRef.current) onChange?.(editorRef.current.innerHTML)
    }

    const handleInput = () => {
        if (editorRef.current) onChange?.(editorRef.current.innerHTML)
    }

    const handleLink = () => {
        const url = window.prompt('Enter URL')
        if (url) exec('createLink', url)
    }

    return (
        <div className={styles.field}>
            {label && <label className={styles.label}>{label}</label>}

            <div className={styles.toolbar}>
                <button type="button" className={styles.toolBtn} onClick={() => exec('bold')} title="Bold">
                    <Bold size={14} />
                </button>
                <button type="button" className={styles.toolBtn} onClick={() => exec('italic')} title="Italic">
                    <Italic size={14} />
                </button>
                <button type="button" className={styles.toolBtn} onClick={() => exec('formatBlock', 'H2')} title="Heading">
                    <Heading2 size={14} />
                </button>
                <button type="button" className={styles.toolBtn} onClick={() => exec('insertUnorderedList')} title="Bullet list">
                    <List size={14} />
                </button>
                <button type="button" className={styles.toolBtn} onClick={handleLink} title="Insert link">
                    <Link2 size={14} />
                </button>
                <span className={styles.toolbarSpacer} />
                <button
                    type="button"
                    className={`${styles.toolBtn} ${sourceMode ? styles.toolBtnActive : ''}`}
                    onClick={() => setSourceMode((s) => !s)}
                    title={sourceMode ? 'Preview' : 'Edit HTML source'}
                >
                    {sourceMode ? <Eye size={14} /> : <Code2 size={14} />}
                </button>
            </div>

            {sourceMode ? (
                <textarea
                    className={styles.sourceTextarea}
                    rows={10}
                    value={value || ''}
                    onChange={(e) => onChange?.(e.target.value)}
                    spellCheck={false}
                />
            ) : (
                <div
                    ref={editorRef}
                    className={styles.editor}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={handleInput}
                    dangerouslySetInnerHTML={{ __html: value || '' }}
                    data-placeholder={placeholder}
                />
            )}
        </div>
    )
}

export default MicrositeRichTextEditor